import { Component, OnInit, OnDestroy, OnChanges, Input, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { FinancialStatementAccount } from '../../models/common/FinancialStatementAccount';
import { TrialBalanceService } from '../../services/trial-balance/trial-balance.service';
import { ColumnSort } from 'src/app/domain/shared/component/sort-arrow/sort-arrow.component';
import { FsExportExcelService } from '../../services/common/fs-export-excel.service';
import { SortService } from '../../../company/service/sort.service';
import {
  AccountFilterData,
  CheckboxState,
  EmitterFlags,
  FsExcelExportType,
  ManualAccountObjectForEmitter,
} from '../../utils/common/utilityClasses';
import {
  ThinkTwiceModalController,
  TrialBalanceTotals,
  checkForZeroBalance,
  getCountOfAllFilteredOutAccountsWithZeroBalance,
} from '../../utils/trial-balance/utils';

@Component({
  selector: 'app-trial-balance',
  templateUrl: './trial-balance.component.html',
  styleUrls: ['./trial-balance.component.scss'],
})
export class TrialBalanceComponent implements OnInit, OnDestroy, OnChanges {

  private _destroyStream: Subject<void> = new Subject<void>();
  @Input() tbData: FinancialStatementAccount[] = [];
  @Input() isDrilldown = false; // is this component a drilldown from BS/PL/Assembly
  @Input() showTrialBalanceNavBar = true;
  @Input() isLocked = false;
  @Input() isBooksTab = false;
  @Input() accountFilterDataObject: AccountFilterData;
  @Input() isManualAccounts: boolean;
  @Output() manualAccountEmitter: EventEmitter<ManualAccountObjectForEmitter> = new EventEmitter<ManualAccountObjectForEmitter>();
  @Output() bulkDeleteEmitter: EventEmitter<FinancialStatementAccount[]> = new EventEmitter<FinancialStatementAccount[]>();
  manualAccountObjectForEmitter: ManualAccountObjectForEmitter = {
    action: undefined,
    manualAccount: undefined,
  };

  totals = new TrialBalanceTotals();
  switcher = 1;
  reservedAccounts: FinancialStatementAccount[] = [];
  reservedTbData: FinancialStatementAccount[] = [];
  sortArray: ColumnSort[] = [];
  accountToBeDeleted: FinancialStatementAccount = undefined;
  // input value for search account needs to clean input by clearData()
  searchValue: string;
  selectedAccountForDrilldown: FinancialStatementAccount;
  manualAccounts: FinancialStatementAccount[] = [];
  bulkAccountDeleteObject: BulkAccountDeleteObject;
  accountSorting = AccountSummarySorting;
  show = {
    manualAccounts: true,
    existingAccounts: true,
    glDrilldown: false, // hide or show transactions drilldown
  };

  zeroBalanceAccHelper: ZeroBalanceAccountsActionWrapper = {
    show: 'Show accounts with 0 balance',
    hide: 'Hide accounts with 0 balance',
    textForAccounts: '',
    areAccountsWithZeroBalanceHidden: false,
    countOfFilteredAccountWithZeroBalance: 0,
  };

  thinkTwiceModalController: ThinkTwiceModalController = {
    isShown: false,
    text: {
      title: 'Delete manual account',
      description: this.tbService.getThinkTwiceModalForManualAccountDescription(this.accountToBeDeleted),
    },
  };

  constructor(
    private tbService: TrialBalanceService,
    private fsExcelService: FsExportExcelService,
  ) { }

  onAccountDrilldownChange(account: FinancialStatementAccount): void {
    this.selectedAccountForDrilldown = account;
  }

  openDrilldown(account: FinancialStatementAccount): void {
    if (!account.glTransactions.length) {
      return;
    }
    this.onAccountDrilldownChange(account);
    this.show.glDrilldown = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tbData || changes.accountFilterDataObject) {
      this.dataSetup();
    }
  }

  ngOnInit(): void {
    this.defineArrayOfSortingColumns();
  }

  showOrHideAccountsWithZeroBalance(): void {
    if (this.zeroBalanceAccHelper.areAccountsWithZeroBalanceHidden) {
      // if we were hiding accounts with 0 balance, we need to show them
      this.showAccountsWithZeroBalance();
    } else {
      this.hideAccountsWithZeroBalance();
    }
  }

  ngOnDestroy(): void {
    this.tbData = [];
    this.totals.totalAfterChanges = 0;
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  handleDeleteManualAccount(accountToBeDeleted: FinancialStatementAccount): void {
    this.accountToBeDeleted = accountToBeDeleted;
    this.thinkTwiceModalController.text.description = this.tbService.getThinkTwiceModalForManualAccountDescription(this.accountToBeDeleted);
    this.thinkTwiceModalController.isShown = true;
  }

  deleteManualAccount(value: boolean): void {
    this.thinkTwiceModalController.isShown = false;
    if (!value) {
      return;
    }
    this.manualAccountObjectForEmitter = {
      action: EmitterFlags.DELETE,
      manualAccount: this.accountToBeDeleted,
    };
    this.manualAccountEmitter.emit(this.manualAccountObjectForEmitter);

  }

  editManualAccount(account: FinancialStatementAccount): void {
    this.manualAccountObjectForEmitter = {
      action: EmitterFlags.EDIT,
      manualAccount: account,
    };
    this.manualAccountEmitter.emit(this.manualAccountObjectForEmitter);
  }

  dataSetup(): void {
    const { tbData, manualAccounts } = this.tbService
      .dataSetup(this.accountFilterDataObject, this.tbData, this.totals, this.manualAccounts);
    this.tbData = tbData;
    this.reservedTbData = tbData;
    this.manualAccounts = manualAccounts;
    this.reservedAccounts = this.manualAccounts;
  }


  exportExcel(): void {
    const exportType: FsExcelExportType = this.isManualAccounts ? FsExcelExportType.MANUAL_ACCOUNTS : FsExcelExportType.TB;
    this.fsExcelService.exportExcel(exportType);
  }

  initBulkDeleteMode(): void {
    this.bulkAccountDeleteObject = this.tbService.initBulkDeleteMode();
  }

  closeBulkDelete(wasConfirmed: boolean): void {
    if (wasConfirmed) {
      this.bulkDeleteEmitter.emit(this.bulkAccountDeleteObject.selectedAccounts);
    }
    this.bulkAccountDeleteObject = null;
    this.manualAccounts.forEach((account) => account.isSelectedForDelete = false);
  }

  selectForBulkDelete(account: FinancialStatementAccount, isSelected: boolean): void {
    account.isSelectedForDelete = isSelected;
    if (isSelected) {
      this.bulkAccountDeleteObject.selectedAccounts.push(account);
    } else {
      this.bulkAccountDeleteObject.selectedAccounts.filter((acc) => acc.id !== account.id);
    }
    this.setBulkDeleteAllCheckbox();
  }

  toggleBulkDeleteAll(isSelected: boolean): void {
    this.manualAccounts.forEach((account) => this.selectForBulkDelete(account, isSelected));
  }

  setBulkDeleteAllCheckbox(): void {
    this.bulkAccountDeleteObject.bulkDeleteAllCheckbox = this.tbService.
      setBulkDeleteAllCheckbox(this.bulkAccountDeleteObject, this.manualAccounts.length);
  }

  filterAccountsByNameOrCode(inputValue: string): void {
    this.searchValue = inputValue;
    if (!inputValue) {
      this.clearFilter();
      return;
    }
    const { filteredManualAccounts, filteredTbData } = this.tbService
      .filterAccountsByNameOrCode(inputValue, this.reservedAccounts, this.reservedTbData);
    this.manualAccounts = [...filteredManualAccounts];
    this.tbData = [...filteredTbData];
  }

  clearFilter(): void {
    this.searchValue = undefined;
    this.manualAccounts = [...this.reservedAccounts];
    this.tbData = [...this.reservedTbData];
  }

  sort(field: string): void {
    const current = this.sortArray.find((el) => el.field === field);
    const coef = current.counter === 0 ? 1 : -1;
    this.tbData.sort((a, b) => coef * this.compareElements(a, b, field));
    this.manualAccounts.sort((a, b) => coef * this.compareElements(a, b, field));
    current.counter = current.counter === 1 ? 0 : current.counter + 1;
    this.sortArray.forEach((el) => (el.field !== field ? el.counter = -1 : undefined));
  }

  hideAccountsWithZeroBalance(): void {
    this.tbData.forEach((acc) => acc.showInTb = checkForZeroBalance(acc.sums?.balance));
    this.manualAccounts.forEach((acc) => acc.showInTb = checkForZeroBalance(acc.sums?.balance));
    this.zeroBalanceAccHelper.countOfFilteredAccountWithZeroBalance =
    getCountOfAllFilteredOutAccountsWithZeroBalance(this.tbData, this.manualAccounts);
    this.zeroBalanceAccHelper.areAccountsWithZeroBalanceHidden = true;
    this.zeroBalanceAccHelper.textForAccounts = this.zeroBalanceAccHelper
      .countOfFilteredAccountWithZeroBalance === 1 ? 'account' : 'accounts';
  }

  showAccountsWithZeroBalance(): void {
    this.tbData.forEach((acc) => acc.showInTb = true);
    this.manualAccounts.forEach((acc) => acc.showInTb = true);
    this.zeroBalanceAccHelper.areAccountsWithZeroBalanceHidden = false;
  }

  private compareElements(a: FinancialStatementAccount, b: FinancialStatementAccount, field: string): number {
    if (field === 'diff') {
      const lastA = a.sums.manualDebit - a.sums.manualCredit;
      const lastB = b.sums.manualDebit - b.sums.manualCredit;
      return lastA - lastB;
    }
    return SortService.compareElements(a, b, field);
  }

  private defineArrayOfSortingColumns(): void {
    this.sortArray.push({ field: 'code', counter: 0 });
    this.sortArray.push({ field: 'sums.sumITP', counter: -1 });
    this.sortArray.push({ field: 'diff', counter: -1 });
    this.sortArray.push({ field: 'sums.balance', counter: -1 });
    this.sortArray.push({ field: 'filteredTransactions.length', counter: -1 });
  }

}

export interface BulkAccountDeleteObject {
  isActive: boolean;
  infoText: string;
  selectedAccounts: FinancialStatementAccount[];
  bulkDeleteAllCheckbox: CheckboxState;
}

export interface ZeroBalanceAccountsActionWrapper {
  show: string;
  hide: string;
  areAccountsWithZeroBalanceHidden: boolean;
  textForAccounts: string;
  countOfFilteredAccountWithZeroBalance: number;
}

export const AccountSummarySorting = {
  byAccountCode: (account: FinancialStatementAccount) => account.code,
  byOriginalBalance: (account: FinancialStatementAccount) => account.sums.sumITP,
  byAdjustment: (account: FinancialStatementAccount) => (account.sums.manualDebit - account.sums.manualCredit),
  byAdjustedTotal: (account: FinancialStatementAccount) => account.sums.balance,
  byTransactionCount: (account: FinancialStatementAccount) => account.glTransactions.length,
} as const;
